import {NgFor, NgIf} from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	signal,
} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {TruncatedTextComponent} from '@shared/components/ui';
import {FolderViewDto} from '@shared/modules/library/modules/folders/types/folder-view.dto';
import {VideoDetails} from '@shared/modules/videos/types/video-details';
import {TranslatePipe} from '@shared/pipes';
import {filter, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {
	archivedFoldersSelector,
	currentFolderSelector,
	foldersSelector,
	trashedFoldersSelector,
} from '@shared/modules/library/modules/folders/store/folder.selector';
import {AppState} from 'src/app/store/app.state';
import {getFolderPath} from '@core/helpers/fn/get-folder-path.fn';
import {combineLatest, of, Subject} from 'rxjs';
import {getSectionName} from '@core/helpers/fn/get-section-name.fn';
import {FoldersService} from '@shared/modules/library/modules/folders/services/folder.service';

@Component({
	standalone: true,
	selector: 'green-bread-crumb',
	imports: [
		NgFor,
		NgIf,
		MatButtonModule,
		MatMenuModule,
		MatIconModule,
		TranslatePipe,
		TruncatedTextComponent,
	],
	template: `<ng-container *ngIf="showLibrary && data.length; else noData">
			<a mat-button (click)="onSetCurrentFolder(null)" class="dark:!text-white">
				<mat-icon class="dark:!text-white !m-0 justify-normal">{{
					defaultRootIcon ?? sectionIcon()
				}}</mat-icon>
			</a>
			<ng-container *ngIf="firstPart.length > 0">
				<span class="inline-flex">
					<mat-icon class="dark:!text-white">keyboard_arrow_right</mat-icon>
				</span>
				<button
					mat-icon-button
					[matMenuTriggerFor]="menu"
					class="dark:!text-white">
					<mat-icon class="dark:!text-white">more_horiz</mat-icon>
				</button>
				<mat-menu #menu="matMenu">
					<!-- <button mat-menu-item>
						<a mat-button>
							<p>
								{{ 'VIEWS.USER_BOARD.LIBRARY.HEADER.TITLE' | translate }}
							</p>
						</a>
					</button> -->
					<button
						mat-menu-item
						items-center
						h-full
						*ngFor="let bc of firstPart">
						<a mat-button (click)="onSetCurrentFolder(bc)">
							<green-truncated-text
								[value]="bc.name"
								[maxLength]="25"
								class="text-sm truncate" />
						</a>
					</button>
				</mat-menu>
			</ng-container>
			<span class="inline-flex" *ngFor="let bc of secondPart">
				<mat-icon class="!pt-3 dark:!text-white"
					>keyboard_arrow_right</mat-icon
				>
				<a mat-button (click)="onSetCurrentFolder(bc)" class="dark:!text-white">
					<green-truncated-text
						[value]="bc.name"
						[maxLength]="20"
						class="text-sm dark:!text-white" />
				</a>
			</span>
		</ng-container>
		<ng-template #noData>
			<ng-container *ngIf="defaultRootIcon !== null">
				<a mat-button (click)="onSetCurrentFolder(null)" class="dark:!text-white">
					<mat-icon class="dark:!text-white !m-0 justify-normal">{{
						defaultRootIcon
					}}</mat-icon>
				</a>
			</ng-container>
			<ng-container *ngIf="defaultRootIcon === null">
				<a
					mat-button
					(click)="onBack()"
					class="is-bold w-fit flex flex-wrap justify-start items-center"
					color="primary">
					<mat-icon>arrow_back</mat-icon>
					{{ 'VIEWS.USER_BOARD.VIDEO_DETAILS.BACK_TO_LIBRARY' | translate }}
				</a>
			</ng-container>
		</ng-template>`,
})
export class BreadCrumbComponent implements OnInit, OnChanges, OnDestroy {
	@Input() showLibrary: boolean = true;
	@Input() isAdmin: boolean;
	@Input() video: VideoDetails;
	@Input() data: FolderViewDto[] = [];
	@Input() skipReset: boolean = false;
	@Input() defaultRootIcon: 'home' | 'delete' | 'archive' | null = null;

	@Output() backTo = new EventEmitter();
	@Output() setCurrentFolder = new EventEmitter<FolderViewDto | null>();

	firstPart: FolderViewDto[] = [];
	secondPart: FolderViewDto[] = [];

	@HostBinding('class') class = 'flex items-center';

	sectionIcon = signal<'archive' | 'home' | 'delete' | null>('home');

	private destroy$ = new Subject<void>();
	private dataProvidedExternally = false;

	constructor(
		private readonly router: Router,
		private readonly ref: ChangeDetectorRef,
		private readonly folderService: FoldersService,
		private store: Store<AppState>
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['data']) {
			this.dataProvidedExternally = true;
			this.updateBreadCrumb(this.data);
		}
	}

	ngOnInit() {
		if (this.video && this.video.path) {
			this.updateBreadCrumb(this.video.path ?? []);
			return;
		}

		if (this.dataProvidedExternally) {
			return;
		}

		this.store.select(currentFolderSelector).pipe(
			takeUntil(this.destroy$),
			switchMap(currentFolder => {
				if (!currentFolder?.uuid) {
					return of([] as FolderViewDto[]);
				}
				return combineLatest([
					this.store.select(foldersSelector),
					this.store.select(trashedFoldersSelector),
					this.store.select(archivedFoldersSelector),
				]).pipe(
					map(([folders, trash, archive]) => [...folders, ...trash, ...archive]),
					filter(allFolders => allFolders.length > 0),
					take(1),
					map(allFolders => getFolderPath(allFolders, currentFolder.uuid))
				);
			})
		).subscribe(path => this.updateBreadCrumb(path));
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	updateBreadCrumb(path: FolderViewDto[]): void {
		if (path) {
			this.data = [...path];

			this.secondPart = this.data;
			this.firstPart = [];

			if (this.data.length > 3) {
				this.secondPart = this.data.splice(-3);
				this.firstPart = this.data;
			}

			this.setSection();
			this.ref.detectChanges();
		}
	}

	onSetCurrentFolder(folder: FolderViewDto | null): void {
		if (this.skipReset && !folder) {
			return;
		}

		if (this.video) {
			const libraryUrl =
				this.isAdmin && this.video
					? `/vidmizer-board/account/${this.video.account.uuid}?tab=videos`
					: '/app/library';

			this.folderService.setCurrentFolder(folder, getSectionName(this.video));
			this.router.navigateByUrl(libraryUrl);
		} else {
			this.setCurrentFolder.emit(folder);
		}
	}

	onBack = () => {
		this.backTo.emit();
	};

	setSection = () => {
		if (!this.data.length) {
			this.sectionIcon.set(null);
			return;
		}
		// When built from video.path, the API returns partial folder data without
		// isInTrash/isArchived fields. Use the video's own flags instead.
		const source: { isInTrash: boolean; isArchived: boolean } =
			this.video ?? this.data[0];
		if (source?.isInTrash) {
			this.sectionIcon.set('delete');
		} else if (source?.isArchived) {
			this.sectionIcon.set('archive');
		} else {
			this.sectionIcon.set('home');
		}
	};
}
